import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { Menu, X, ShieldCheck, LayoutDashboard, LogOut, User } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/gallery", label: "Gallery" },
  { to: "/sell", label: "For Artists" },
  { to: "/trade", label: "Trade & Designers" },
  { to: "/how-it-works", label: "How It Works" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const dashPath = user
    ? user.role === "admin" ? "/admin" : user.role === "artist" ? "/artist" : "/buyer"
    : "/login";

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[var(--bg-primary)]/90 border-b border-[var(--border-subtle)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img src="/artfutur-mixed.png" alt="ArtFutur — Artists Meet Collectors"
              className="h-14 sm:h-16 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <Link key={l.to} to={l.to}
                data-testid={`nav-link-${l.label.split(" ")[0].toLowerCase()}`}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-[var(--gold)] ${
                  location.pathname === l.to ? "text-[var(--gold)]" : "text-[var(--text-secondary)]"
                }`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu"
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-subtle)] hover:border-[var(--gold)] transition-colors">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium max-w-[120px] truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => navigate(dashPath)} data-testid="nav-dashboard-item">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout-item">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn"
                  className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                  Sign in
                </Link>
                <Link to="/register" data-testid="nav-register-btn"
                  className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold">
                  Join ARTFUTUR
                </Link>
              </>
            )}
          </div>

          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--border-subtle)] bg-[var(--bg-primary)] px-4 py-4 space-y-3">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block text-sm font-medium text-[var(--text-secondary)] py-1">{l.label}</Link>
          ))}
          <div className="pt-3 border-t border-[var(--border-subtle)] flex flex-col gap-2">
            {user ? (
              <>
                <Link to={dashPath} onClick={() => setOpen(false)} className="text-sm font-medium">Dashboard</Link>
                <button onClick={handleLogout} className="text-sm text-left text-[var(--text-secondary)]">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)}
                  className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold text-center">Join ARTFUTUR</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
